import { BriefingItemDto } from '../../types/opsBriefing';
import { MemberView } from './adapters';
import { FALLBACK_REREGISTRATION_RATE } from './memberValue';
import { ValueBreakdownRow } from './ValueBreakdownModal';

/**
 * 「왜 이 금액인가」 팝업에 들어갈 **줄**을 만든다.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 화면이 아니라 여기인가
 * ─────────────────────────────────────────────────────────────
 * 같은 팝업이 세 화면에서 열립니다 — 오늘의 브리핑 헤더, 회원 현황 헤더,
 * 회원 현황 단계 카드. 문구를 화면마다 적으면 「지켜야 할 매출」의 정의가
 * 화면마다 조금씩 달라지고, 그러면 원장님은 세 화면이 다른 것을 세고 있다고
 * 읽습니다. 정의·정책 문구·줄 만드는 규칙을 여기 한 곳에 둡니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 **여기서 금액을 만들지 않습니다**
 * ─────────────────────────────────────────────────────────────
 * 금액도, 「잔여 15회 × 55,000원」 같은 근거 문자열도 전부 계산기
 * (`memberValue.computeMemberValue`)가 만든 것을 **그대로 옮깁니다.**
 * 여기서 `잔여 × 단가` 를 다시 조립하면 같은 식이 두 곳에 생기고,
 * 계산기가 예외를 하나 더 두는 순간 팝업과 합계가 조용히 갈립니다.
 *
 * ⚠️ 합계도 만들지 않습니다. 팝업의 합계는 **화면이 이미 그린 그 변수**를
 *    그대로 넘깁니다. 그래서 이 함수에 넘기는 목록은 그 합계를 만든
 *    **바로 그 목록**이어야 합니다 — 다르면 내역을 더한 값이 합계와 안 맞습니다.
 */

/* ── 정의와 정책 문구 ─────────────────────────────────────── */

export const PREPAID_DEFINITION =
    '이미 받았지만 아직 수업하지 않은 금액(미사용 선수금)입니다 — 잔여 회차 × 회당 단가. ' +
    '회계로는 부채(이연수익)이고, 회원이 안 나오거나 환불하면 센터가 실제로 잃는 돈입니다.';

export const EXPECTED_DEFINITION =
    '다음 수강권 예상액 × 순번별 재등록률입니다. 확률을 곱한 기댓값이라 확정 매출이 아닙니다.';

export const PREPAID_NOTES = [
    '재등록 기대는 더하지 않습니다. 이미 받은 돈과 앞으로 받을 기댓값은 성격이 다릅니다.',
    '회차 개념이 없는 수강권(무제한·대관)과 회당 단가를 모르는 수강권은 빠져 있습니다.',
    '환불 규정에 따라 실제 지급액은 달라질 수 있습니다.',
];

/**
 * 재등록 기대의 정책 문구.
 *
 * ⚠️ 「기대하지 않는 자리」의 이름이 화면마다 다릅니다. 회원 현황은 회원
 *    **단계**(이탈 위험·주의)로, 오늘의 브리핑은 **등급**(이탈 위험·휴면)으로
 *    같은 판정을 합니다 (`adapters.expectsReRegistration` ↔
 *    `briefingValue.expectsReRegistrationByGrade`). 판정은 같고 이름만 다르므로
 *    이름만 받아서 문장을 만듭니다.
 */
export const expectedNotes = (zeroLabel: string): string[] => [
    '다음 수강권 예상액은 직전과 같은 금액으로 보수적으로 잡았습니다.',
    `${zeroLabel}은 재등록을 기대하지 않아 0입니다.`,
    '수강권이 여러 건인 회원도 대표 수강권 한 건에서만 셉니다 — 다음 결제를 여러 번 세면 없는 매출이 만들어집니다.',
];

export const UNPRICED_REASON =
    '회당 단가와 결제 금액이 없거나(0원 포함) 회차 개념이 없는 수강권(무제한·대관)입니다. 0원이라는 뜻이 아닙니다';

/** 순번을 확정하지 못한 줄에 붙는 단서 — 「보수적」이라고 적지 않는다 */
const UNVERIFIED_RATE_NOTE =
    `순번을 확정할 수 없어 3번째 값 ${FALLBACK_REREGISTRATION_RATE}% 를 적용했습니다 ` +
    '— 알려진 셋 중 가장 높은 값이라 금액이 위로 치우쳤을 수 있습니다';

/* ── 회원 현황 (MemberView) ───────────────────────────────── */

/**
 * 💰 미사용 선수금 줄 — **카드(수강권)마다 한 줄**.
 *
 * 선수금은 수강권마다 따로 받은 돈이라 카드별로 더한 값이 곧 회원별 합계입니다
 * (`adapters.sumPrepaid`). 그래서 넘어온 목록을 그대로 폅니다.
 */
export const memberPrepaidRows = (cards: MemberView[]): ValueBreakdownRow[] =>
    cards
        .filter((m) => m.value?.prepaid != null)
        .map((m) => ({
            key: m.cardKey,
            name: m.name,
            sub: `${m.coachName} 강사 · ${m.product}`,
            basis: m.value?.prepaidBasis ?? null,
            amount: m.value?.prepaid ?? null,
        }));

/**
 * 📈 재등록 기대 줄 — **회원마다 한 줄**.
 *
 * ⚠️ 반드시 대표 수강권만 남긴 목록(`adapters.pickPrimaryCards`)을 넘기세요.
 *    전체 목록을 넘기면 수강권이 셋인 회원의 다음 결제가 세 번 잡혀
 *    내역의 합이 헤더보다 커집니다.
 */
export const memberExpectedRows = (primaryCards: MemberView[]): ValueBreakdownRow[] =>
    primaryCards
        .filter((m) => m.value?.expected != null)
        .map((m) => ({
            key: m.cardKey,
            name: m.name,
            sub: `${m.coachName} 강사 · ${m.product}`,
            basis: m.value?.expectedBasis ?? null,
            amount: m.value?.expected ?? null,
            /*
             * 0 은 「모른다」가 아니라 「이 자리에서는 기대하지 않기로 했다」이다.
             * 이유를 안 적으면 「돈이 안 걸린 회원」으로 읽힌다 (정책 M-D2).
             */
            note:
                m.value?.expected === 0
                    ? '이 단계에서는 재등록을 기대하지 않습니다'
                    : m.value && !m.value.rateVerified
                      ? UNVERIFIED_RATE_NOTE
                      : null,
        }));

/** 금액을 한 푼도 못 낸 카드 수 — 합계에서 빠진 건을 밝히는 데 쓴다 */
export const countUnpricedCards = (cards: MemberView[]): number =>
    cards.filter((m) => m.value == null).length;

/* ── 오늘의 브리핑 (BriefingItemDto) ──────────────────────── */

const itemSub = (item: BriefingItemDto): string | null => {
    const parts = [
        item.relatedCoachName ? `${item.relatedCoachName} 강사` : null,
        item.membership?.product ?? null,
    ].filter((v): v is string => v != null);
    return parts.length > 0 ? parts.join(' · ') : null;
};

export const itemPrepaidRows = (items: BriefingItemDto[]): ValueBreakdownRow[] =>
    items
        .filter((i) => i.subjectType === 'MEMBER' && i.value?.prepaid != null)
        .map((i) => ({
            key: i.itemId,
            name: i.subjectName,
            sub: itemSub(i),
            basis: i.value?.prepaidBasis ?? null,
            amount: i.value?.prepaid ?? null,
        }));

/**
 * ⚠️ 회원 중복을 이미 걷어낸 목록을 넘기세요 (`briefingValue` 의 `dedupeBySubject`).
 *    같은 회원이 하루에 두 번 올라온 날 내역이 헤더보다 커집니다.
 */
export const itemExpectedRows = (items: BriefingItemDto[]): ValueBreakdownRow[] =>
    items
        .filter((i) => i.subjectType === 'MEMBER' && i.value?.expected != null)
        .map((i) => ({
            key: i.itemId,
            name: i.subjectName,
            sub: itemSub(i),
            basis: i.value?.expectedBasis ?? null,
            amount: i.value?.expected ?? null,
            note: i.value?.expected === 0 ? '이 등급에서는 재등록을 기대하지 않습니다' : null,
        }));
